import { DEFAULT_MASTER_SEED } from '../data/mastersCatalog'
import { apiJsonRequest } from './apiClient'
import {
  catalogMasterPayloadFromInput,
  createCatalogMaster,
  deleteCatalogMaster,
  listCatalogMasters,
  updateCatalogMaster,
  type CatalogMasterRecord,
  type CatalogMasterResource,
} from './catalogMastersService'

export type MasterKey =
  | 'payors'
  | 'plans'
  | 'contract-types'
  | 'billing-states'
  | 'submission-methods'
  | 'plan-types'
  | 'networks'
  | 'product-services'
  | 'cpt-categories'
  | 'authorization-statuses'
  | 'timely-filing-limits'
  | 'corrected-claim-deadlines'
  | 'coordination-of-benefits'
  | 'assignment-of-benefits'
  | 'claim-frequency-types'
  | 'rendering-provider-required'
  | 'modifiers'
  | 'fee-basis'
  | 'rental-cap-periods'
  | 'cap-resets-on'
  | 'equipment-categories'
  | 'transfer-triggers'
  | 'ownership-transfer-modifiers'
  | 'documentation-required'
  | 'pa-submission-methods'
  | 'standard-turnaround-times'

export type MasterOption = {
  /** Backend row id when loaded from API (e.g. payor master id). */
  id?: string
  value: string
  label: string
  isActive?: boolean
  sortOrder?: number
  meta?: Record<string, unknown>
}

export type MasterRequestOptions = {
  useApi?: boolean
  /** Passed to list endpoints that support server-side search (e.g. payors `?q=`). */
  q?: string
}

/** Master modules backed by REST APIs instead of local seed storage. */
export const API_BACKED_MASTER_KEYS: ReadonlySet<MasterKey> = new Set([
  'payors',
  'plans',
  'product-services',
  'cpt-categories',
  'authorization-statuses',
  'contract-types',
  'billing-states',
  'submission-methods',
  'plan-types',
  'networks',
  'timely-filing-limits',
  'corrected-claim-deadlines',
  'coordination-of-benefits',
  'assignment-of-benefits',
  'claim-frequency-types',
  'rendering-provider-required',
  'modifiers',
  'fee-basis',
  'rental-cap-periods',
  'cap-resets-on',
  'equipment-categories',
  'transfer-triggers',
  'ownership-transfer-modifiers',
  'documentation-required',
  'pa-submission-methods',
  'standard-turnaround-times',
])

const CATALOG_MASTER_RESOURCES: Partial<Record<MasterKey, CatalogMasterResource>> = {
  payors: 'payors',
  plans: 'plans',
  'product-services': 'product-services',
  'cpt-categories': 'cpt-categories',
  'authorization-statuses': 'authorization-statuses',
  'contract-types': 'contract-types',
  'billing-states': 'billing-states',
  'submission-methods': 'submission-methods',
  'plan-types': 'plan-types',
  networks: 'networks',
  'timely-filing-limits': 'timely-filing-limits',
  'corrected-claim-deadlines': 'corrected-claim-deadlines',
  'coordination-of-benefits': 'coordination-of-benefits',
  'assignment-of-benefits': 'assignment-of-benefits',
  'claim-frequency-types': 'claim-frequency-types',
  'rendering-provider-required': 'rendering-provider-required',
  modifiers: 'modifiers',
  'fee-basis': 'fee-basis',
  'rental-cap-periods': 'rental-cap-periods',
  'cap-resets-on': 'cap-resets-on',
  'equipment-categories': 'equipment-categories',
  'transfer-triggers': 'transfer-triggers',
  'ownership-transfer-modifiers': 'ownership-transfer-modifiers',
  'documentation-required': 'documentation-required',
  'pa-submission-methods': 'pa-submission-methods',
  'standard-turnaround-times': 'standard-turnarounds',
}

function catalogResourceForKey(key: MasterKey): CatalogMasterResource | null {
  return CATALOG_MASTER_RESOURCES[key] ?? null
}

export function isApiBackedMasterKey(key: MasterKey): boolean {
  return API_BACKED_MASTER_KEYS.has(key)
}

export function masterRowKey(option: MasterOption): string {
  return option.id ?? option.value
}

export type CreateMasterOptionInput = {
  value: string
  label: string
  isActive?: boolean
  sortOrder?: number
  meta?: Record<string, unknown>
}

export type UpdateMasterOptionInput = Partial<CreateMasterOptionInput>

type MastersApiResponse = {
  data?: unknown[]
  items?: unknown[]
}

const STORAGE_KEY = 'healthlynk_masters_v1'
export const MASTERS_UPDATED_EVENT = 'healthlynk:masters-updated'

function cloneSeed(): Record<MasterKey, MasterOption[]> {
  return JSON.parse(JSON.stringify(DEFAULT_MASTER_SEED)) as Record<MasterKey, MasterOption[]>
}

function loadFromStorage(): Record<MasterKey, MasterOption[]> | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Record<MasterKey, MasterOption[]>>
    const merged = cloneSeed()
    for (const key of Object.keys(merged) as MasterKey[]) {
      if (Array.isArray(parsed[key])) {
        merged[key] = parsed[key]!
      }
    }
    return merged
  } catch {
    return null
  }
}

function saveToStorage(data: Record<MasterKey, MasterOption[]>) {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

function notifyMastersUpdated() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(MASTERS_UPDATED_EVENT))
}

let localMasters: Record<MasterKey, MasterOption[]> = loadFromStorage() ?? cloneSeed()

function getStore(): Record<MasterKey, MasterOption[]> {
  return localMasters
}

function persistAndNotify() {
  saveToStorage(localMasters)
  notifyMastersUpdated()
}

function catalogRecordToOption(record: CatalogMasterRecord): MasterOption {
  return {
    id: record.id,
    value: record.value_key,
    label: record.display_label,
    isActive: record.status === 'active',
    sortOrder: record.sort_order,
    meta: record,
  }
}

function normalizeMasterOption(value: unknown): MasterOption | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>

  const rawValue = row.value ?? row.id ?? row.code ?? row.slug
  const rawLabel = row.label ?? row.name ?? row.description ?? rawValue

  if (rawValue == null || rawLabel == null) return null

  const optionValue = String(rawValue).trim()
  const optionLabel = String(rawLabel).trim()
  if (!optionValue || !optionLabel) return null

  return {
    id: typeof row.id === 'string' ? row.id.trim() : undefined,
    value: optionValue,
    label: optionLabel,
    isActive: typeof row.isActive === 'boolean' ? row.isActive : undefined,
    sortOrder:
      typeof row.sortOrder === 'number'
        ? row.sortOrder
        : typeof row.order === 'number'
          ? row.order
          : undefined,
    meta: row,
  }
}

export function sortMasterOptions(options: MasterOption[]): MasterOption[] {
  return [...options].sort((a, b) => {
    const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER
    const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER
    if (ao !== bo) return ao - bo
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  })
}

export function getActiveMasterOptions(options: MasterOption[]): MasterOption[] {
  return sortMasterOptions(options.filter((o) => o.isActive !== false))
}

export async function fetchMasterOptions(
  key: MasterKey,
  opts?: MasterRequestOptions
): Promise<MasterOption[]> {
  const useApi = opts?.useApi ?? isApiBackedMasterKey(key)
  if (!useApi) {
    return getActiveMasterOptions(getStore()[key] ?? [])
  }

  const catalogResource = catalogResourceForKey(key)
  if (catalogResource) {
    const records = await listCatalogMasters(catalogResource, opts?.q)
    return getActiveMasterOptions(records.map(catalogRecordToOption))
  }

  const response = await apiJsonRequest<MastersApiResponse>(`/masters/${encodeURIComponent(key)}`, {
    method: 'GET',
  })
  const rawItems = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.items)
      ? response.items
      : []
  const options = rawItems.map(normalizeMasterOption).filter(Boolean) as MasterOption[]
  return sortMasterOptions(options)
}

export async function fetchAllMasterOptions(
  key: MasterKey,
  opts?: MasterRequestOptions
): Promise<MasterOption[]> {
  const useApi = opts?.useApi ?? isApiBackedMasterKey(key)
  if (!useApi) {
    return sortMasterOptions(getStore()[key] ?? [])
  }

  const catalogResource = catalogResourceForKey(key)
  if (catalogResource) {
    const records = await listCatalogMasters(catalogResource, opts?.q)
    return sortMasterOptions(records.map(catalogRecordToOption))
  }

  return fetchMasterOptions(key, opts)
}

export async function addMasterOption(
  key: MasterKey,
  input: CreateMasterOptionInput,
  opts?: MasterRequestOptions
): Promise<MasterOption> {
  const useApi = opts?.useApi ?? isApiBackedMasterKey(key)
  if (!useApi) {
    const existing = getStore()[key] ?? []
    const next: MasterOption = {
      value: input.value.trim(),
      label: input.label.trim(),
      isActive: input.isActive ?? true,
      sortOrder: input.sortOrder ?? existing.length + 1,
      meta: input.meta,
    }
    localMasters = {
      ...localMasters,
      [key]: [...existing, next],
    }
    persistAndNotify()
    return next
  }

  const catalogResource = catalogResourceForKey(key)
  if (catalogResource) {
    const record = await createCatalogMaster(catalogResource, catalogMasterPayloadFromInput(input))
    return catalogRecordToOption(record)
  }

  const response = await apiJsonRequest<{ data?: unknown; item?: unknown }>(
    `/masters/${encodeURIComponent(key)}`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  )
  const normalized = normalizeMasterOption(response?.data ?? response?.item)
  if (!normalized) {
    throw new Error('Master saved but response format is invalid.')
  }
  return normalized
}

export async function updateMasterOption(
  key: MasterKey,
  value: string,
  input: UpdateMasterOptionInput,
  opts?: MasterRequestOptions
): Promise<MasterOption> {
  const useApi = opts?.useApi ?? isApiBackedMasterKey(key)
  if (!useApi) {
    const existing = getStore()[key] ?? []
    let updated: MasterOption | null = null
    const nextList = existing.map((item) => {
      if (item.value !== value) return item
      updated = {
        ...item,
        ...(input.label !== undefined ? { label: input.label.trim() } : {}),
        ...(input.value !== undefined ? { value: input.value.trim() } : {}),
        ...(input.isActive !== undefined ? { isActive: input.isActive } : {}),
        ...(input.sortOrder !== undefined ? { sortOrder: input.sortOrder } : {}),
        ...(input.meta !== undefined ? { meta: input.meta } : {}),
      }
      return updated
    })
    if (!updated) {
      throw new Error('Master value not found.')
    }
    localMasters = { ...localMasters, [key]: nextList }
    persistAndNotify()
    return updated
  }

  const catalogResource = catalogResourceForKey(key)
  if (catalogResource) {
    if (!input.label?.trim() || !input.value?.trim()) {
      throw new Error('Display label and value key are required to update this master.')
    }
    const record = await updateCatalogMaster(
      catalogResource,
      value,
      catalogMasterPayloadFromInput({
        label: input.label,
        value: input.value,
        isActive: input.isActive,
      })
    )
    return catalogRecordToOption(record)
  }

  const response = await apiJsonRequest<{ data?: unknown; item?: unknown }>(
    `/masters/${encodeURIComponent(key)}/${encodeURIComponent(value)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(input),
    }
  )
  const normalized = normalizeMasterOption(response?.data ?? response?.item)
  if (!normalized) {
    throw new Error('Master updated but response format is invalid.')
  }
  return normalized
}

export async function deleteMasterOption(
  key: MasterKey,
  value: string,
  opts?: MasterRequestOptions
): Promise<void> {
  const useApi = opts?.useApi ?? isApiBackedMasterKey(key)
  if (!useApi) {
    const existing = getStore()[key] ?? []
    localMasters = {
      ...localMasters,
      [key]: existing.filter((item) => item.value !== value),
    }
    persistAndNotify()
    return
  }

  const catalogResource = catalogResourceForKey(key)
  if (catalogResource) {
    await deleteCatalogMaster(catalogResource, value)
    return
  }

  await apiJsonRequest(`/masters/${encodeURIComponent(key)}/${encodeURIComponent(value)}`, {
    method: 'DELETE',
  })
}

export async function toggleMasterOptionActive(
  key: MasterKey,
  value: string,
  isActive: boolean,
  opts?: MasterRequestOptions & { label?: string; valueKey?: string }
): Promise<MasterOption> {
  return updateMasterOption(
    key,
    value,
    {
      ...(opts?.label !== undefined ? { label: opts.label } : {}),
      ...(opts?.valueKey !== undefined ? { value: opts.valueKey } : {}),
      isActive,
    },
    opts
  )
}

export function resetMastersToDefaults(key?: MasterKey) {
  if (key) {
    localMasters = {
      ...localMasters,
      [key]: JSON.parse(JSON.stringify(DEFAULT_MASTER_SEED[key])),
    }
  } else {
    localMasters = cloneSeed()
  }
  persistAndNotify()
}
